import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Modal, SUB_CONFIRM_LAYER, TextButton, useConfirmDialog } from '@safetyhub/ui';
import { adminApi, extractApiError } from '../lib/api';
import { getMemberKindLabel } from '../lib/roles';
import ImpersonateButton from './ImpersonateButton';
import DeleteUserDialog, { type DeleteUserResult } from './DeleteUserDialog';
import RestoreUserDialog from './RestoreUserDialog';
import { useToastStore } from '../stores/toast';
import { businessMemberships } from '../lib/memberships';

interface User {
  id: string;
  /**
   * 연락처는 Member에 산다. `email`은 **연락 이메일**로 결재 통지가 읽는 축이고,
   * 로그인 이메일(`auths` 중 provider='EMAIL')과는 **다른 칸**이다 (F-H).
   */
  member: { name: string; phone: string | null; email: string | null } | null;
  isSuperAdmin: boolean;
  /** 전 문서 유형 접근 entitlement (조직 category와 무관하게 전 유형 노출·작성 허용) */
  allReportTypes: boolean;
  createdAt: string;
  deletedAt: string | null;
  deletionSource: 'SELF' | 'ADMIN' | null;
  auths: Array<{
    provider: 'EMAIL' | 'ID' | 'KAKAO' | 'GOOGLE';
    providerId: string;
    verified: boolean;
  }>;
  memberships: Array<{
    /** 소유자 성질(배정 축, P7-b-2 — 옛 `role`) */
    isOwner: boolean;
    /** ⚠️ false = 외부 연락처(현장 담당자·외부 결재자). 조직 권한 없음 — `api.ts` 주석 참조 */
    countsAsMember: boolean;
    organization: { id: string; name: string; category: string };
  }>;
}

// 인라인 편집 가능한 사용량 섹션
function UsageEditableSection({
  photoCount,
  inspectionCount,
  photoLimit,
  inspectionLimit,
  lastResetAt,
  hasTodayUsage,
  onUpdate,
  isUpdating,
}: {
  photoCount: number;
  inspectionCount: number;
  photoLimit: number;
  inspectionLimit: number;
  lastResetAt?: string | null;
  hasTodayUsage: boolean;
  onUpdate: (field: 'photoCount' | 'inspectionCount', value: number) => Promise<unknown>;
  isUpdating: boolean;
}) {
  const [editingField, setEditingField] = useState<'photo' | 'inspection' | null>(null);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingField && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingField]);

  // 사용량 진행바 fill. web `getUsageColor`(stores/usage.ts)와 같은 램프를 쓴다.
  const getBarColor = (used: number, limit: number) => {
    const percent = limit > 0 ? (used / limit) * 100 : 100;
    if (percent >= 100) return 'bg-red-500'; // 소진 — danger 의미색
    if (percent >= 80) return 'bg-amber-500'; // 임박 — 목업 세만틱 램프는 yellow가 아닌 amber
    return 'bg-brand-500'; // 정상 — 구 브랜드 blue에서 이관
  };

  const startEdit = (field: 'photo' | 'inspection') => {
    setEditingField(field);
    setEditValue(field === 'photo' ? photoCount.toString() : inspectionCount.toString());
  };

  const handleSave = async () => {
    if (!editingField) return;

    const newValue = parseInt(editValue, 10);
    const currentValue = editingField === 'photo' ? photoCount : inspectionCount;

    if (isNaN(newValue) || newValue < 0 || newValue === currentValue) {
      setEditingField(null);
      return;
    }

    try {
      await onUpdate(editingField === 'photo' ? 'photoCount' : 'inspectionCount', newValue);
      setEditingField(null);
    } catch {
      // 에러는 상위에서 처리
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditingField(null);
    }
  };

  const renderUsageRow = (
    label: string,
    field: 'photo' | 'inspection',
    count: number,
    limit: number,
    unit: string,
  ) => {
    const isEditing = editingField === field;

    return (
      <div>
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-600">{label}</span>
          {isEditing ? (
            <div className="flex items-center gap-1">
              <input
                ref={inputRef}
                type="number"
                min="0"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleSave}
                onKeyDown={handleKeyDown}
                disabled={isUpdating}
                className="w-14 px-1 py-0.5 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <span className="text-gray-400">/ {limit}{unit}</span>
            </div>
          ) : (
            <button
              onClick={() => startEdit(field)}
              className="font-medium hover:text-brand-600 hover:underline cursor-pointer"
              title="클릭하여 수정"
            >
              {count} / {limit}{unit}
            </button>
          )}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${getBarColor(count, limit)}`}
            style={{ width: `${Math.min((count / limit) * 100, 100)}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-50 rounded-card p-4 space-y-3">
      {renderUsageRow('사진 업로드', 'photo', photoCount, photoLimit, '장')}
      {renderUsageRow('점검 생성', 'inspection', inspectionCount, inspectionLimit, '회')}
      {lastResetAt && (
        <p className="text-xs text-gray-400">
          마지막 초기화: {new Date(lastResetAt).toLocaleString('ko-KR')}
        </p>
      )}
      {!hasTodayUsage && (
        <p className="text-sm text-gray-500 text-center py-2">오늘 사용 기록이 없습니다.</p>
      )}
    </div>
  );
}

interface UserDetailModalProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
}

const providerLabels: Record<string, string> = {
  EMAIL: '이메일',
  ID: '아이디',
  KAKAO: '카카오',
  GOOGLE: '구글',
};


export default function UserDetailModal({ user, isOpen, onClose }: UserDetailModalProps) {
  const queryClient = useQueryClient();
  const showToast = useToastStore((state) => state.showToast);
  // 사용량 초기화 확인 — 이 상세(z-[70]) 위에 뜨므로 한 층 위(`SUB_CONFIRM_LAYER`).
  const [confirm, confirmDialog] = useConfirmDialog();
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState(false);

  // 일일 사용량 조회
  const { data: usageData, refetch: refetchUsage } = useQuery({
    queryKey: ['admin', 'user-usage', user?.id],
    queryFn: () => adminApi.getUserUsageHistory(user!.id, 7),
    enabled: isOpen && !!user,
  });

  // 전역 설정 조회
  const { data: configData } = useQuery({
    queryKey: ['admin', 'usage-config'],
    queryFn: () => adminApi.getUsageConfig(),
    enabled: isOpen && !!user,
  });

  // 사용량 초기화
  // 복원 안내 재발송 — 탈퇴 철회보다 **먼저** 시도하는 경로.
  // 링크가 계정 등록 연락처로만 가므로, 본인 확인을 운영자가 판단하지 않아도 된다.
  const resendRestoreNoticeMutation = useMutation({
    mutationFn: () => adminApi.resendRestoreNotice(user!.id),
    onSuccess: (res) => {
      const sent = res.data;
      showToast(
        sent
          ? `복원 안내를 재발송했습니다 (${sent.channels.join('·')}). 남은 횟수 ${sent.remaining}회`
          : '복원 안내를 재발송했습니다.',
        'success',
      );
    },
    onError: (err) => {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    },
  });

  const resetUsageMutation = useMutation({
    mutationFn: () => adminApi.resetUserUsage(user!.id),
    onSuccess: () => {
      showToast('사용량이 초기화되었습니다.', 'success');
      refetchUsage();
      queryClient.invalidateQueries({ queryKey: ['admin', 'usage-users'] });
    },
    onError: (err) => {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    },
  });

  // 사용량 수정
  const updateUsageMutation = useMutation({
    mutationFn: (data: { photoCount?: number; inspectionCount?: number }) =>
      adminApi.updateUserUsage(user!.id, data),
    onSuccess: () => {
      showToast('사용량이 수정되었습니다.', 'success');
      refetchUsage();
      queryClient.invalidateQueries({ queryKey: ['admin', 'usage-users'] });
    },
    onError: (err) => {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    },
  });

  // 로컬 표시 상태 (mutation 성공 시 즉시 반영)
  const [displayName, setDisplayName] = useState(user?.member?.name || '');
  const [displayPhone, setDisplayPhone] = useState(user?.member?.phone || null);
  // F-H: 편집 대상은 **연락 이메일**(Member.email)이다. 로그인 이메일(auths)은 읽기 전용으로 따로 보인다.
  const [displayEmail, setDisplayEmail] = useState<string | null>(user?.member?.email ?? null);
  const [displayDeletedAt, setDisplayDeletedAt] = useState(user?.deletedAt || null);
  const [displayDeletionSource, setDisplayDeletionSource] = useState(user?.deletionSource || null);
  const [displayAllReportTypes, setDisplayAllReportTypes] = useState(user?.allReportTypes ?? false);

  // user prop이 변경되면 로컬 상태 동기화
  useEffect(() => {
    if (user) {
      setDisplayName(user.member?.name ?? '');
      setDisplayPhone(user.member?.phone ?? null);
      setDisplayEmail(user.member?.email ?? null);
      setDisplayDeletedAt(user.deletedAt);
      setDisplayDeletionSource(user.deletionSource);
      setDisplayAllReportTypes(user.allReportTypes ?? false);
    }
  }, [user]);

  const updatePhoneMutation = useMutation({
    mutationFn: (phone: string) => adminApi.updateUserPhone(user!.id, phone),
    onSuccess: (data) => {
      if (data.success) {
        setSuccess('전화번호가 변경되었습니다.');
        setIsEditingPhone(false);
        setDisplayPhone(data.data?.phone || newPhone); // 즉시 반영
        setNewPhone('');
        setError(null);
        queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      } else {
        setError(data.error?.message || '전화번호 변경에 실패했습니다.');
      }
    },
    onError: (err) => {
      const apiError = extractApiError(err);
      setError(apiError.message);
    },
  });

  // 이메일 변경 — 소유권 인증 없이 저장되며 서버가 verified: false로 기록한다 (소급 보정용)
  const updateEmailMutation = useMutation({
    mutationFn: (email: string) => adminApi.updateUserEmail(user!.id, email),
    onSuccess: (data) => {
      if (data.success) {
        // F-H: 이제 연락처(Member.email)에 저장한다 — verified 개념은 로그인 이메일 쪽 것이라
        // 여기서 "미인증"을 말하면 거짓이 된다.
        setSuccess('연락 이메일이 변경되었습니다.');
        setIsEditingEmail(false);
        setDisplayEmail(data.data?.email || newEmail); // 즉시 반영
        setNewEmail('');
        setError(null);
        queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      } else {
        setError(data.error?.message || '이메일 변경에 실패했습니다.');
      }
    },
    onError: (err) => {
      const apiError = extractApiError(err);
      setError(apiError.message);
    },
  });

  const updateNameMutation = useMutation({
    mutationFn: (name: string) => adminApi.updateUserName(user!.id, name),
    onSuccess: (data) => {
      if (data.success) {
        setSuccess('이름이 변경되었습니다.');
        setIsEditingName(false);
        setDisplayName(data.data?.name || newName); // 즉시 반영
        setNewName('');
        setError(null);
        queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      } else {
        setError(data.error?.message || '이름 변경에 실패했습니다.');
      }
    },
    onError: (err) => {
      const apiError = extractApiError(err);
      setError(apiError.message);
    },
  });

  // 전 문서 유형 접근 entitlement 토글
  const updateAllReportTypesMutation = useMutation({
    mutationFn: (allReportTypes: boolean) =>
      adminApi.updateUserAllReportTypes(user!.id, allReportTypes),
    onSuccess: (data) => {
      if (data.success && data.data) {
        setSuccess(data.data.message);
        setDisplayAllReportTypes(data.data.allReportTypes); // 즉시 반영
        setError(null);
        queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      } else {
        setError(data.error?.message || '설정 변경에 실패했습니다.');
      }
    },
    onError: (err) => {
      const apiError = extractApiError(err);
      setError(apiError.message);
    },
  });

  const updatePasswordMutation = useMutation({
    mutationFn: (password: string) => adminApi.updateUserPassword(user!.id, password),
    onSuccess: (data) => {
      if (data.success) {
        setSuccess('비밀번호가 변경되었습니다.');
        setIsEditingPassword(false);
        setNewPassword('');
        setError(null);
        queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      } else {
        setError(data.error?.message || '비밀번호 변경에 실패했습니다.');
      }
    },
    onError: (err) => {
      const apiError = extractApiError(err);
      setError(apiError.message);
    },
  });

  const handleClose = () => {
    setIsEditingPhone(false);
    setNewPhone('');
    setIsEditingEmail(false);
    setNewEmail('');
    setIsEditingName(false);
    setNewName('');
    setIsEditingPassword(false);
    setNewPassword('');
    setError(null);
    setSuccess(null);
    setIsDeleteDialogOpen(false);
    setIsRestoreDialogOpen(false);
    onClose();
  };

  const handleDeleteSuccess = ({ notifyRequested, notified }: DeleteUserResult) => {
    // 통지를 켰는데 안 나갔으면 그대로 말한다 — 성공 문구만 띄우면 갔다고 믿는다.
    setSuccess(
      notifyRequested && !notified
        ? '사용자가 강제 탈퇴 처리되었습니다. (본인 통지는 발송되지 않았습니다 — 등록된 연락처가 없거나 발송에 실패했습니다)'
        : notifyRequested
        ? '사용자가 강제 탈퇴 처리되었습니다. (본인에게 통지 발송)'
        : '사용자가 강제 탈퇴 처리되었습니다.',
    );
    setDisplayDeletedAt(new Date().toISOString());
    setDisplayDeletionSource('ADMIN');
    setIsDeleteDialogOpen(false);
    queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
  };

  const handleRestoreSuccess = () => {
    setSuccess('탈퇴가 철회되었습니다.');
    setDisplayDeletedAt(null);
    setDisplayDeletionSource(null);
    setIsRestoreDialogOpen(false);
    queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // 숫자만 허용
    const value = e.target.value.replace(/[^0-9]/g, '');
    setNewPhone(value.slice(0, 11)); // 최대 11자리
  };

  const handlePhoneSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const phoneRegex = /^01[0-9]\d{7,8}$/;

    if (!phoneRegex.test(newPhone)) {
      setError('휴대폰 번호 형식이 올바르지 않습니다. (예: 01012345678)');
      return;
    }

    updatePhoneMutation.mutate(newPhone);
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const trimmedEmail = newEmail.trim();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('올바른 이메일 형식이 아닙니다.');
      return;
    }

    updateEmailMutation.mutate(trimmedEmail);
  };

  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const trimmedName = newName.trim();

    if (trimmedName.length < 2 || trimmedName.length > 50) {
      setError('이름은 2~50자여야 합니다.');
      return;
    }

    updateNameMutation.mutate(trimmedName);
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (newPassword.length < 8) {
      setError('비밀번호는 최소 8자 이상이어야 합니다.');
      return;
    }

    const passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])/;
    if (!passwordRegex.test(newPassword)) {
      setError('비밀번호는 영문, 숫자, 특수문자를 포함해야 합니다.');
      return;
    }

    updatePasswordMutation.mutate(newPassword);
  };

  const formatPhone = (phone: string | null): string => {
    if (!phone) return '-';
    if (phone.length === 11) {
      return `${phone.slice(0, 3)}-${phone.slice(3, 7)}-${phone.slice(7)}`;
    }
    if (phone.length === 10) {
      return `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6)}`;
    }
    return phone;
  };

  if (!user) return null;

  // 로그인 식별자 표시
  const idAuth = user.auths.find((a) => a.provider === 'ID');
  const emailAuth = user.auths.find((a) => a.provider === 'EMAIL');
  const kakaoAuth = user.auths.find((a) => a.provider === 'KAKAO');
  const googleAuth = user.auths.find((a) => a.provider === 'GOOGLE');

  return (
    <Modal open={isOpen} onClose={handleClose} title="사용자 상세 정보" size="lg">
      <div className="space-y-6">
        {/* 기본 정보 */}
        <div>
              <h4 className="text-sm font-medium text-gray-500 mb-3">기본 정보</h4>
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-xs text-gray-500">ID</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono">{user.id}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">이름</dt>
                  <dd className="mt-1">
                    {isEditingName ? (
                      <form onSubmit={handleNameSubmit} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                          placeholder={user.member?.name ?? ''}
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                          autoFocus
                          maxLength={50}
                        />
                        <Button
                          type="submit"
                          variant="primary"
                          size="xs"
                          disabled={updateNameMutation.isPending}
                        >
                          {updateNameMutation.isPending ? '...' : '저장'}
                        </Button>
                        <Button
                          variant="secondary"
                          size="xs"
                          onClick={() => {
                            setIsEditingName(false);
                            setNewName('');
                            setError(null);
                          }}
                        >
                          취소
                        </Button>
                      </form>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-900">{displayName}</span>
                        <Button
                          variant="secondary"
                          size="xs"
                          onClick={() => {
                            setIsEditingName(true);
                            setNewName(displayName);
                          }}
                        >
                          변경
                        </Button>
                      </div>
                    )}
                  </dd>
                </div>
                {idAuth && (
                  <div>
                    <dt className="text-xs text-gray-500">아이디</dt>
                    <dd className="mt-1 text-sm text-gray-900">{idAuth.providerId}</dd>
                  </div>
                )}
                {/*
                  F-H: 로그인 이메일은 **읽기 전용**이다. 운영자가 여기서 값을 넣으면 소유권이
                  증명되지 않은 주소에 로그인 권한을 주게 되고(`login`이 EMAIL/ID를 OR로 조회),
                  unique 충돌로 막히면서 정작 결재 통지는 나가지 않는다. 통지에 쓰이는 값은
                  아래 "이메일(연락처)" 칸이다.
                */}
                {emailAuth && (
                  <div>
                    <dt className="text-xs text-gray-500">
                      로그인 이메일
                      {!emailAuth.verified && <span className="ml-1 text-amber-600">(미인증)</span>}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">{emailAuth.providerId}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-xs text-gray-500">
                    이메일(연락처)
                  </dt>
                  <dd className="mt-1">
                    {isEditingEmail ? (
                      <form onSubmit={handleEmailSubmit} className="flex items-center gap-2">
                        <input
                          type="email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          placeholder="user@example.com"
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                          autoFocus
                          maxLength={255}
                        />
                        <Button
                          type="submit"
                          variant="primary"
                          size="xs"
                          disabled={updateEmailMutation.isPending}
                        >
                          {updateEmailMutation.isPending ? '...' : '저장'}
                        </Button>
                        <Button
                          variant="secondary"
                          size="xs"
                          onClick={() => {
                            setIsEditingEmail(false);
                            setNewEmail('');
                            setError(null);
                          }}
                        >
                          취소
                        </Button>
                      </form>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-900">{displayEmail ?? '-'}</span>
                        <Button
                          variant="secondary"
                          size="xs"
                          onClick={() => {
                            setIsEditingEmail(true);
                            setNewEmail(displayEmail ?? '');
                          }}
                        >
                          {displayEmail ? '변경' : '등록'}
                        </Button>
                      </div>
                    )}
                  </dd>
                </div>
                {kakaoAuth && (
                  <div>
                    <dt className="text-xs text-gray-500">카카오</dt>
                    <dd className="mt-1 text-sm text-gray-900">연동됨</dd>
                  </div>
                )}
                {googleAuth && (
                  <div>
                    <dt className="text-xs text-gray-500">구글</dt>
                    <dd className="mt-1 text-sm text-gray-900">연동됨</dd>
                  </div>
                )}
                <div>
                  <dt className="text-xs text-gray-500">가입일</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(user.createdAt).toLocaleDateString('ko-KR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </dd>
                </div>
              </dl>
            </div>

            {/* 전화번호 - 일반/탈퇴대기 사용자만 표시 */}
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-3">전화번호</h4>
              {isEditingPhone ? (
                <form onSubmit={handlePhoneSubmit} className="space-y-3">
                  <div>
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={newPhone}
                      onChange={handlePhoneChange}
                      placeholder="01012345678"
                      className="w-full px-3 py-2 border border-gray-300 rounded-control focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      autoFocus
                      maxLength={11}
                    />
                    <p className="mt-1 text-xs text-gray-500">숫자만 입력 (최대 11자리)</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      variant="primary"
                      size="sm"
                      disabled={updatePhoneMutation.isPending}
                    >
                      {updatePhoneMutation.isPending ? '변경 중...' : '변경'}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setIsEditingPhone(false);
                        setNewPhone('');
                        setError(null);
                      }}
                    >
                      취소
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-900">{formatPhone(displayPhone)}</span>
                  <Button variant="secondary" size="xs" onClick={() => setIsEditingPhone(true)}>
                    변경
                  </Button>
                </div>
              )}
            </div>

            {/* 비밀번호 변경 - ID/EMAIL 인증이 있는 일반/탈퇴대기 사용자만 표시 */}
            {(user.auths.some(a => a.provider === 'ID') || user.auths.some(a => a.provider === 'EMAIL')) && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-3">비밀번호</h4>
                {isEditingPassword ? (
                  <form onSubmit={handlePasswordSubmit} className="space-y-3">
                    <div>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="새 비밀번호"
                        className="w-full px-3 py-2 border border-gray-300 rounded-control focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                        autoFocus
                      />
                      <p className="mt-1 text-xs text-gray-500">8자 이상, 영문/숫자/특수문자 포함</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="submit"
                        variant="primary"
                        size="sm"
                        disabled={updatePasswordMutation.isPending}
                      >
                        {updatePasswordMutation.isPending ? '변경 중...' : '변경'}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setIsEditingPassword(false);
                          setNewPassword('');
                          setError(null);
                        }}
                      >
                        취소
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-500">••••••••</span>
                    <Button variant="secondary" size="xs" onClick={() => setIsEditingPassword(true)}>
                      변경
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* 메시지 */}
            {error && (
              <div className="p-3 bg-red-50 text-red-600 text-sm rounded-card">{error}</div>
            )}
            {success && (
              <div className="p-3 bg-green-50 text-green-600 text-sm rounded-card">{success}</div>
            )}

            {/* 계정 타입 - 일반/탈퇴대기 사용자만 표시 */}
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-3">계정 정보</h4>
              <div className="flex flex-wrap gap-2">
                {user.auths.map((auth, index) => (
                  <span
                    key={index}
                    className={`px-2 py-1 text-xs rounded-full ${
                      auth.verified
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}
                  >
                    {providerLabels[auth.provider] || auth.provider}
                    {!auth.verified && ' (미인증)'}
                  </span>
                ))}
                {user.isSuperAdmin && (
                  <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-full">
                    슈퍼관리자
                  </span>
                )}
              </div>
            </div>

            {/* 문서 유형 접근 entitlement — 계정 전역 속성 (결정 기록 2026-08-07) */}
            {!user.isSuperAdmin && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-3">문서 유형 접근</h4>
                <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={displayAllReportTypes}
                    onChange={(e) => updateAllReportTypesMutation.mutate(e.target.checked)}
                    disabled={updateAllReportTypesMutation.isPending || !!displayDeletionSource}
                    className="mt-0.5 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span>
                    모든 문서 유형 접근
                    <span className="block text-xs text-gray-500 mt-0.5">
                      조직 유형과 무관하게 문서 유형 선택에서 전 유형을 노출하고 작성을 허용합니다.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {/* 소속 조직 (개인 조직 제외) - 일반/탈퇴대기 사용자만 표시 */}
            {(() => {
              // 개인/팀(WORKSPACE)과 외부 연락처(role null) 제외 — 목록 화면과 같은 술어를
              // 쓴다. 외부 연락처는 조직 권한이 없어 "소속 조직"이 아니다(`lib/memberships`).
              const orgMemberships = businessMemberships(user.memberships);
              return (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-3">
                    소속 조직 ({orgMemberships.length}개)
                  </h4>
                  {orgMemberships.length > 0 ? (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {orgMemberships.map((membership, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-card"
                        >
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {membership.organization.name}
                            </div>
                          </div>
                          <span className="px-2 py-1 text-xs bg-brand-100 text-brand-700 rounded">
                            {getMemberKindLabel(membership)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">소속된 조직이 없습니다.</p>
                  )}
                </div>
              );
            })()}

            {/* 일일 사용량 */}
            {usageData?.success && configData?.success && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-500">오늘의 사용량</h4>
                  {usageData.data?.history && usageData.data.history.length > 0 && (
                    <TextButton
                      onClick={async () => {
                        if (
                          await confirm({
                            title: '사용량 초기화',
                            body: `${user.member?.name ?? '사용자'}님의 오늘 사용량을 초기화하시겠습니까?`,
                            confirmLabel: '초기화',
                            danger: true,
                            ...SUB_CONFIRM_LAYER,
                          })
                        ) {
                          resetUsageMutation.mutate();
                        }
                      }}
                      disabled={resetUsageMutation.isPending}
                      className="px-2 py-1 text-xs hover:bg-brand-50"
                    >
                      {resetUsageMutation.isPending ? '초기화 중...' : '초기화'}
                    </TextButton>
                  )}
                </div>
                <UsageEditableSection
                  photoCount={usageData.data?.history?.[0]?.photoCount ?? 0}
                  inspectionCount={usageData.data?.history?.[0]?.inspectionCount ?? 0}
                  photoLimit={configData.data?.photoLimit ?? 100}
                  inspectionLimit={configData.data?.inspectionLimit ?? 5}
                  lastResetAt={usageData.data?.history?.[0]?.lastResetAt}
                  hasTodayUsage={!!usageData.data?.history?.[0]}
                  onUpdate={(field, value) => updateUsageMutation.mutateAsync({ [field]: value })}
                  isUpdating={updateUsageMutation.isPending}
                />
              </div>
            )}

        {/* 탈퇴 대기 상태 표시 */}
        {displayDeletionSource && (
          <div className="p-3 border rounded-card bg-orange-50 border-orange-200">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="ml-2 text-sm text-orange-800">
                이 사용자는 탈퇴 대기 상태입니다. 복원 가능합니다.
                {displayDeletionSource === 'SELF' ? ' (자발적 탈퇴)' : ' (관리자 강제 탈퇴)'}
                {displayDeletedAt && ` - ${new Date(displayDeletedAt).toLocaleDateString('ko-KR')}`}
              </span>
            </div>
          </div>
        )}

        {/* 액션 */}
        {!user.isSuperAdmin && (
          <div className="pt-4 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-500 mb-3">액션</h4>
            <div className="flex flex-wrap gap-2">
              {!displayDeletionSource && (
                <ImpersonateButton userId={user.id} userName={user.member?.name ?? ''} />
              )}
              {displayDeletionSource ? (
                // 탈퇴 상태에서 할 수 있는 건 되돌리기뿐이다 — '완전 삭제'(하드삭제)는 P8에서
                // 제거됐고, 파기는 30일 뒤 USER_PURGE가 익명화로 수행한다.
                //
                // ⚠️ 순서가 정책이다: **재발송 먼저, 직접 철회는 폴백.**
                // 재발송은 링크가 등록 연락처로만 가서 본인 확인이 연락처 소유로 증명되지만,
                // 직접 철회는 "제 계정인데요"라는 문의를 운영자가 믿어야 해서 사칭에 열려 있다.
                <>
                  <button
                    onClick={() => resendRestoreNoticeMutation.mutate()}
                    disabled={resendRestoreNoticeMutation.isPending}
                    title="계정에 등록된 연락처로만 발송됩니다"
                    className="px-4 py-2 text-sm font-medium text-brand-600 bg-brand-50 border border-brand-200 rounded-control hover:bg-brand-100 disabled:opacity-50"
                  >
                    {resendRestoreNoticeMutation.isPending
                      ? '재발송 중...'
                      : '복원 안내 재발송'}
                  </button>
                  <button
                    onClick={() => setIsRestoreDialogOpen(true)}
                    title="연락처로 연락이 닿지 않을 때만 사용하세요"
                    className="px-4 py-2 text-sm font-medium text-green-600 bg-green-50 border border-green-200 rounded-control hover:bg-green-100"
                  >
                    탈퇴 철회
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-control hover:bg-red-100"
                >
                  강제 탈퇴
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 삭제 다이얼로그 */}
      <DeleteUserDialog
        user={user}
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onSuccess={handleDeleteSuccess}
      />

      {/* 복원 다이얼로그 */}
      <RestoreUserDialog
        user={user}
        isOpen={isRestoreDialogOpen}
        onClose={() => setIsRestoreDialogOpen(false)}
        onSuccess={handleRestoreSuccess}
      />
      {confirmDialog}
    </Modal>
  );
}
